import { getURL, setHeaders } from "../../../Apis/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { openModal } from "../../Errors/Slices/errorSlice";
import { fetchQuestionHeader } from "../../Questions/Actions/QuestionActions";

const fetchAssessments = createAsyncThunk(
  "assessments/fetchAssessments",
  async (name, thunkAPI) => {
    try {
      const Axios = getURL();
      const exams = await Axios.get("/exams", setHeaders());
      return exams.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchAssessmentsByAuthor = createAsyncThunk(
  "assessments/fetchAssessmentsByAuthor",
  async (name, thunkAPI) => {
    try {
      const userLocal = JSON.parse(sessionStorage.getItem("user"));
      const userId = userLocal?.userid;
      const Axios = getURL();
      const exams = await Axios.get(`/exams?createdby=${userId}`, setHeaders());
      return exams.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchAssessmentsToday = createAsyncThunk(
  "assessments/fetchAssessmentsToday",
  async (name, thunkAPI) => {
    try {
      const Axios = getURL();
      const today = Date.now();
      const exams = await Axios.get(
        `/exams?createdfrom=${today}&createdto=${today}`,
        setHeaders()
      );
      return exams.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchAssessment = createAsyncThunk(
  "assessments/fetchAssessment",
  async (name, thunkAPI) => {
    try {
      const Axios = getURL();
      const exam = await Axios.get(`/exams/${name}`, setHeaders());
      thunkAPI.dispatch(fetchSections(name));
      return exam.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchFormattedAssessment = createAsyncThunk(
  "assessments/fetchFormattedAssessment",
  async (examId, thunkAPI) => {
    try {
      console.log("fetch Formatted Assessment triggered");
      const Axios = getURL();
      // Formatting helper functions
      const formatOptions = (options) => {
        return options.map((option) => {
          // OPTION STRUCTURE
          return {
            optionID: option.qoid,
            text: option.opttext,
          };
        });
      };

      const formatQuestions = (questions) => {
        return questions.map((question) => {
          // QUESTION STRUCTURE
          const formattedQuestion = {
            questionID: question.qid,
            text: question.question,
            typeName: question.qtype.typename,
            instructions: question.qtype.instructions,
            score: question.score,
          };
          const questionHasOptions = question.hasOwnProperty("options");
          questionHasOptions
            ? (formattedQuestion.options = formatOptions(question.options))
            : (formattedQuestion.options = []);
          return formattedQuestion;
        });
      };

      const formatDetails = (assessment) => {
        return {
          examID: assessment.exid,
          title: assessment.examtitle,
          instructions: assessment.instructions,
          duration: assessment.duration.stringfull,
          passMark: assessment.passmark,
          passPercent: assessment.passpercent,
          score: assessment.score,
          showResults: false,
        };
      };

      const formatSections = (sections) => {
        return sections.map((section) => {
          // SECTION STRUCTURE
          return {
            sectionID: section.esid,
            header: section.sectionheader,
            title: section.sectionname,
            passmark: section.passmark,
            passpercent: section.passpercent,
            score: section.score,
            isShuffled: section.qhrandom,
            duration: section.duration?.stringshort || null,
          };
        });
      };

      const formatHeaders = (headers) => {
        return headers.map((header) => {
          // HEADER STRUCTURE
          const formattedHeader = {
            title: header.admintitle,
            images: header.images,
            videos: header.videos,
            categories: header.categories,
          };

          if (header.questioncount > 0) {
            formattedHeader.questions = formatQuestions(header.questions);
          } else {
            formattedHeader.questions = [];
          }
          return formattedHeader;
        });
      };

      // Fetch assessment data and section data in parallel
      const [examResponse, sectionsResponse] = await Promise.all([
        Axios.get(`/exams/${examId}`, setHeaders()),
        Axios.get(`/exams/${examId}/sections`, setHeaders()),
      ]);

      const exam = examResponse.data.data;
      const sections = sectionsResponse.data.data;

      // Fetch questions for all sections in parallel with error handling
      const headersPromises = sections.map(async (section) => {
        try {
          const response = await Axios.get(
            `/examsections/${section.esid}/questions`,
            setHeaders()
          );
          const headers = response?.data?.data;
          return {
            sectionID: section.esid,
            questionHeaders: headers,
            formattedHeaders: formatHeaders(headers),
          };
        } catch (error) {
          console.error(
            `Error fetching questions for section ${section.esid}:`,
            error
          );
          return {
            sectionID: section.esid,
            questionHeaders: [],
            formattedHeaders: [],
          };
        }
      });

      // Wait for all question fetches to complete
      const headersResults = await Promise.allSettled(headersPromises);

      const headers = headersResults.map((result) =>
        result.status === "fulfilled" ? result.value : null
      );

      // FORMATTED ASSESSMENT DATA
      const formattedAssessmentData = {
        details: formatDetails(exam),
        sections: formatSections(sections),
        headers,
        flatQuestionStructure: null,
      };
      return formattedAssessmentData;
    } catch (error) {
      console.error("Error in fetchAssessment:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.errormessage || "An error occurred"
      );
    }
  }
);

const fetchAssessmentAudit = createAsyncThunk(
  "assessments/fetchAssessmentAudit",
  async (name, thunkAPI) => {
    try {
      const Axios = getURL();
      const audit = await Axios.get(`/exams/${name}/changes`, setHeaders());
      return audit.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchSections = createAsyncThunk(
  "assessments/fetchSections",
  async (examId, thunkAPI) => {
    try {
      const Axios = getURL();
      const sections = await Axios.get(
        `/exams/${examId}/sections`,
        setHeaders()
      );
      sections.data.data.forEach((section) => {
        thunkAPI.dispatch(fetchQuestionsBySection(section.esid));
      });
      return sections.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchSection = createAsyncThunk(
  "assessments/fetchSection",
  async (sectionId, thunkAPI) => {
    try {
      const Axios = getURL();
      const section = await Axios.get(
        `/examsections/${sectionId}`,
        setHeaders()
      );
      const questions = await Axios.get(
        `/examsections/${sectionId}/questions`,
        setHeaders()
      );
      return section.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const addSection = createAsyncThunk(
  "assessments/addSection",
  async (name, thunkAPI) => {
    try {
      const Axios = getURL();
      const sections = await Axios.post(`/examsections`, name, setHeaders());
      const url = sections.data.data.api_url.split("/");
      const esid = url[url.length - 1];
      return esid;
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const fetchQuestionsBySection = createAsyncThunk(
  "assessments/fetchQuestionsBySection",
  async (sectionId, thunkAPI) => {
    try {
      const Axios = getURL();
      const questions = await Axios.get(
        `/examsections/${sectionId}/questions`,
        setHeaders()
      );
      return { sectionid: sectionId, questions: questions.data.data };
    } catch (error) {
      return { sectionid: sectionId, questions: [] };
    }
  }
);

const addQuestionBySection = createAsyncThunk(
  "assessments/addQuestionBySection",
  async ({ esid, qhid }, thunkAPI) => {
    try {
      const Axios = getURL();
      await Axios.post(
        `/examsections/${esid}/questions`,
        { qhid },
        setHeaders()
      );
      return thunkAPI.dispatch(fetchQuestionHeader(qhid)).then((question) => {
        return { sectionid: esid, question: question.payload };
      });
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const deleteQuestionBySection = createAsyncThunk(
  "assessments/deleteQuestionBySection",
  async ({ esid, qhid }, thunkAPI) => {
    try {
      const Axios = getURL();
      await Axios.delete(
        `/examsections/${esid}/questions/${qhid}`,
        setHeaders()
      );
      return { sectionid: esid, qhid };
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const createAssessment = createAsyncThunk(
  "assessments/createAssessment",
  async (data, thunkAPI) => {
    try {
      const Axios = getURL();
      const response = await Axios.post("/exams", data, setHeaders());
      const url = response.data.data.api_url;
      const urlArr = url.split("/");
      const id = urlArr[urlArr.length - 1];
      const pathname = window.location.pathname;
      window.location.href = `${pathname}/${id}`;

      return response.data.data;
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const updateAssessment = createAsyncThunk(
  "assessments/updateAssessment",
  async ({ data, examId }, thunkAPI) => {
    try {
      const Axios = getURL();
      const exams = await Axios.put(`/exams/${examId}`, data, setHeaders());
      thunkAPI.dispatch(
        openModal({
          message: "This assessment has been successfully updated",
          severity: "success",
        })
      );
      return thunkAPI.dispatch(fetchAssessment(examId));
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const deleteAssessment = createAsyncThunk(
  "assessments/deleteAssessment",
  async ({ exid, navigate }, thunkAPI) => {
    try {
      const Axios = getURL();
      await Axios.delete(`/exams/${exid}`, setHeaders());
      navigate("/admin/assessments", { replace: true });
      return;
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: "This assessment cannot be deleted, it may still be in use.",
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const updateSection = createAsyncThunk(
  "assessments/updateSection",
  async ({ esid, sectionState }, thunkAPI) => {
    try {
      const Axios = getURL();
      await Axios.put(`/examsections/${esid}`, sectionState, setHeaders());
      thunkAPI.dispatch(
        openModal({
          message: "This section has been updated correctly.",
          severity: "success",
        })
      );
      return thunkAPI.dispatch(fetchSection(esid));
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const deleteSection = createAsyncThunk(
  "assessments/deleteSection",
  async ({ esid }, thunkAPI) => {
    try {
      const Axios = getURL();
      await Axios.delete(`/examsections/${esid}`, setHeaders());

      return esid;
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

const adjustOrder = createAsyncThunk(
  "assessments/adjustOrder",
  async ({ esid, qhid, adjust_order }, thunkAPI) => {
    try {
      const Axios = getURL();
      const exams = await Axios.put(
        `/examsections/${esid}/questions/${qhid} `,
        { adjust_order },
        setHeaders()
      );
      return thunkAPI.dispatch(
        openModal({
          message: "The order of this question has been correctly adjusted.",
          severity: "success",
        })
      );
    } catch (error) {
      thunkAPI.dispatch(
        openModal({
          message: error.response.data.errormessage,
          severity: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response.data.errormessage);
    }
  }
);

export {
  fetchAssessments,
  fetchAssessmentsByAuthor,
  fetchAssessmentsToday,
  fetchAssessment,
  fetchFormattedAssessment,
  fetchAssessmentAudit,
  createAssessment,
  updateAssessment,
  deleteAssessment,
  fetchSections,
  fetchSection,
  addSection,
  updateSection,
  deleteSection,
  fetchQuestionsBySection,
  addQuestionBySection,
  deleteQuestionBySection,
  adjustOrder,
};
